/**
 * CRMContactDetail.jsx — F-02: Contact Detail View
 *
 * Resolves the audit finding NAV-02: /crm/contacts/:contactId previously
 * rendered the contacts list page. This component renders the full contact
 * record, related activities, and account link.
 *
 * API surface:
 *   GET /api/crm/contacts/:contactId — contact record (tenant-scoped)
 *   GET /api/crm/activities?contact_id=:id — related activities
 *
 * Authorization:
 *   Requires authenticated session with tenant_id in JWT.
 *   Missing/cross-tenant contacts return 404.
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  User, Mail, Phone, Building2, ArrowLeft,
  MessageSquare, Calendar, Activity, FileText,
  AlertTriangle, Loader2, Tag, Clock
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const BRAND = {
  primary:  "#7A1A2B",
  bg:       "#FAF6F0",
  cream:    "#FFFFFF",
  gold:     "#C9A15F",
  border:   "#EDE8DE",
  muted:    "#78716C",
  text:     "#1C1917",
};

// ── Helpers ────────────────────────────────────────────────────────────────

function fmt(dt) {
  if (!dt) return "—";
  try { return new Date(dt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return "—"; }
}

function InfoRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2.5 border-b last:border-0" style={{ borderColor: BRAND.border }}>
      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
        style={{ background: `${BRAND.primary}12` }}>
        <Icon className="w-4 h-4" style={{ color: BRAND.primary }} />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-medium mb-0.5" style={{ color: BRAND.muted }}>{label}</p>
        <p className="text-sm font-medium truncate" style={{ color: BRAND.text }}>{value}</p>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────

export default function CRMContactDetail() {
  const { contactId } = useParams();
  const navigate = useNavigate();

  const [contact,    setContact]    = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);

  const load = useCallback(async () => {
    if (!contactId) { setError("No contact ID provided."); setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const [cRes, aRes] = await Promise.allSettled([
        axios.get(`${API}/crm/contacts/${contactId}`, { withCredentials: true }),
        axios.get(`${API}/crm/activities`, { params: { contact_id: contactId, limit: 5 }, withCredentials: true }),
      ]);
      if (cRes.status === "rejected") {
        const status = cRes.reason?.response?.status;
        if (status === 404) setError("Contact not found or not accessible.");
        else if (status === 403) setError("You do not have permission to view this contact.");
        else setError("Unable to load contact. Please try again.");
        return;
      }
      setContact(cRes.value.data);
      if (aRes.status === "fulfilled") {
        const d = aRes.value.data;
        setActivities([
          ...(d.upcoming || []),
          ...(d.overdue  || []),
          ...(d.completed?.slice(0, 3) || []),
        ].slice(0, 5));
      }
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => { load(); }, [load]);

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: BRAND.bg }}>
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: BRAND.primary }} />
          <p className="text-sm" style={{ color: BRAND.muted }}>Loading contact…</p>
        </div>
      </div>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="min-h-screen p-6 flex items-start justify-center" style={{ background: BRAND.bg }}>
        <div className="max-w-md w-full mt-16">
          <button data-testid="contact-detail-back-btn" onClick={() => navigate("/crm/contacts")}
            className="flex items-center gap-2 text-sm mb-6 hover:underline" style={{ color: BRAND.primary }}>
            <ArrowLeft className="w-4 h-4" /> Back to Contacts
          </button>
          <div className="rounded-2xl p-8 text-center shadow-sm" style={{ background: BRAND.cream, border: `1px solid ${BRAND.border}` }}>
            <AlertTriangle className="w-10 h-10 mx-auto mb-3" style={{ color: BRAND.gold }} />
            <h2 className="text-lg font-semibold mb-1" style={{ color: BRAND.text }}>Contact Not Found</h2>
            <p className="text-sm" style={{ color: BRAND.muted }}>{error}</p>
            <button data-testid="contact-detail-go-list-btn"
              onClick={() => navigate("/crm/contacts")}
              className="mt-5 px-4 py-2 rounded-lg text-sm font-medium text-white transition-opacity hover:opacity-90"
              style={{ background: BRAND.primary }}>
              View All Contacts
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!contact) return null;

  const initials = (contact.name || "?")
    .split(" ").slice(0, 2).map(s => s[0]?.toUpperCase() || "").join("");

  // ── Contact Detail ─────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen" style={{ background: BRAND.bg }} data-testid="crm-contact-detail-page">
      {/* Header */}
      <div className="px-6 py-4 border-b flex items-center gap-3"
        style={{ background: BRAND.cream, borderColor: BRAND.border }}>
        <button data-testid="contact-detail-back-btn"
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0">
          <ArrowLeft className="w-4 h-4" style={{ color: BRAND.primary }} />
        </button>
        <div>
          <nav className="text-xs mb-0.5" style={{ color: BRAND.muted }}>
            <Link to="/crm/contacts" className="hover:underline" style={{ color: BRAND.primary }}>Contacts</Link>
            <span className="mx-1">/</span>
            <span>{contact.name}</span>
          </nav>
          <h1 className="text-lg font-semibold" style={{ color: BRAND.text }}>Contact Record</h1>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-5">

        {/* Identity card */}
        <div className="rounded-2xl p-6 shadow-sm flex gap-5 flex-col sm:flex-row"
          style={{ background: BRAND.cream, border: `1px solid ${BRAND.border}` }}>
          {/* Avatar */}
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 text-xl font-bold text-white"
            style={{ background: BRAND.primary }}>
            {initials}
          </div>
          {/* Name + role */}
          <div className="flex-1">
            <h2 className="text-xl font-semibold" style={{ color: BRAND.text }}
              data-testid="contact-detail-name">{contact.name}</h2>
            {contact.title && (
              <p className="text-sm mt-0.5" style={{ color: BRAND.muted }}
                data-testid="contact-detail-title">{contact.title}</p>
            )}
            {contact.account_name && (
              <Link to={contact.account_id ? `/crm/accounts/${contact.account_id}` : "#"}
                className="inline-flex items-center gap-1.5 text-sm mt-2 hover:underline font-medium"
                style={{ color: BRAND.primary }} data-testid="contact-detail-account-link">
                <Building2 className="w-3.5 h-3.5" />
                {contact.account_name}
              </Link>
            )}
          </div>
          {/* Quick actions */}
          <div className="flex flex-col sm:items-end gap-2">
            {contact.email && (
              <a href={`mailto:${contact.email}`}
                data-testid="contact-detail-email-action"
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors hover:bg-gray-50"
                style={{ borderColor: BRAND.border, color: BRAND.text }}>
                <Mail className="w-3.5 h-3.5" style={{ color: BRAND.primary }} /> Send Email
              </a>
            )}
            {contact.phone && (
              <a href={`tel:${contact.phone}`}
                data-testid="contact-detail-phone-action"
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors hover:bg-gray-50"
                style={{ borderColor: BRAND.border, color: BRAND.text }}>
                <Phone className="w-3.5 h-3.5" style={{ color: BRAND.primary }} /> Call
              </a>
            )}
          </div>
        </div>

        {/* Two-column grid: Contact info + Activities */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

          {/* Contact information */}
          <div className="rounded-2xl p-5 shadow-sm"
            style={{ background: BRAND.cream, border: `1px solid ${BRAND.border}` }}>
            <h3 className="text-sm font-semibold mb-3" style={{ color: BRAND.text }}>Contact Information</h3>
            <InfoRow icon={Mail}     label="Email"   value={contact.email} />
            <InfoRow icon={Phone}    label="Phone"   value={contact.phone} />
            <InfoRow icon={Tag}      label="Role"    value={contact.role || contact.contact_role} />
            <InfoRow icon={Calendar} label="Added"   value={fmt(contact.created_at)} />
            <InfoRow icon={Clock}    label="Updated" value={fmt(contact.updated_at)} />
          </div>

          {/* Recent activities */}
          <div className="rounded-2xl p-5 shadow-sm"
            style={{ background: BRAND.cream, border: `1px solid ${BRAND.border}` }}>
            <h3 className="text-sm font-semibold mb-3" style={{ color: BRAND.text }}>Recent Activities</h3>
            {activities.length === 0 ? (
              <div className="py-6 text-center">
                <Activity className="w-7 h-7 mx-auto mb-2 opacity-30" style={{ color: BRAND.primary }} />
                <p className="text-xs" style={{ color: BRAND.muted }}>No activities yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {activities.map((act, i) => (
                  <div key={act.id || i} className="flex items-start gap-2.5 py-2 border-b last:border-0"
                    style={{ borderColor: BRAND.border }}>
                    <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ background: `${BRAND.gold}20` }}>
                      <MessageSquare className="w-3 h-3" style={{ color: BRAND.gold }} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium truncate" style={{ color: BRAND.text }}>
                        {act.title || act.task_type || "Activity"}
                      </p>
                      <p className="text-[11px] mt-0.5" style={{ color: BRAND.muted }}>
                        {fmt(act.due_date || act.completed_at || act.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Notes */}
        {contact.notes && (
          <div className="rounded-2xl p-5 shadow-sm"
            style={{ background: BRAND.cream, border: `1px solid ${BRAND.border}` }}>
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-4 h-4" style={{ color: BRAND.primary }} />
              <h3 className="text-sm font-semibold" style={{ color: BRAND.text }}>Notes</h3>
            </div>
            <p className="text-sm whitespace-pre-wrap leading-relaxed" style={{ color: BRAND.muted }}
              data-testid="contact-detail-notes">{contact.notes}</p>
          </div>
        )}

      </div>
    </div>
  );
}
